import {Icon} from '@wandb/components';
import React, {useEffect, useRef} from 'react';

import {useRunSearchQuery} from '../../../generated/graphql';
import {runContainsFilter, toMongo} from '../../../util/filters';

type RunNode = {
  name: string;
  displayName: string | null;
};

export const useRunMentionSearch = (
  entityName: string,
  projectName: string,
  query: string
): RunNode[] => {
  const filters = query
    ? JSON.stringify(toMongo(runContainsFilter(query, 'run', 'displayName')))
    : '{}';
  const {data} = useRunSearchQuery({
    variables: {entityName, projectName, filters, first: 20},
  });
  return (data?.project?.runs?.edges.map(({node}) => node) ?? []) as RunNode[];
};

export const WBAgentRunMentionDropdown = ({
  runs,
  highlightedIndex,
  onHover,
  onSelect,
}: {
  runs: RunNode[];
  highlightedIndex: number;
  onHover: (index: number) => void;
  onSelect: (displayName: string, name: string) => void;
}) => {
  const highlightedRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (highlightedIndex === 0) {
      containerRef.current?.scrollTo({top: 0});
    } else {
      highlightedRef.current?.scrollIntoView({block: 'nearest'});
    }
  }, [highlightedIndex]);

  return (
    <div
      className="absolute bottom-full left-0 mb-4 w-full max-h-[200px] overflow-y-auto overscroll-none [scrollbar-color:#0d0f1252_transparent] rounded-[5px] border border-moon-250 bg-white shadow-md"
      role="listbox"
      ref={containerRef}>
      <div className="px-10 py-4 text-base font-semibold text-moon-800">
        Runs
      </div>
      {runs.map((run, i) => (
        <div
          key={run.name}
          ref={i === highlightedIndex ? highlightedRef : undefined}
          role="option"
          aria-selected={i === highlightedIndex}
          className={`flex w-full cursor-pointer items-center gap-8 px-10 py-6 text-left text-[15px] leading-[22px] font-normal ${
            i === highlightedIndex ? 'bg-[#0d0f1208]' : ''
          }`}
          onMouseMove={() => onHover(i)}
          onClick={() => {
            if (run.displayName) {
              onSelect(run.displayName, run.name);
            }
          }}>
          <Icon icon="running-repeat" />{' '}
          <span className="truncate">{run.displayName}</span>
        </div>
      ))}
      {runs.length === 0 && (
        <div className="px-12 py-8 text-moon-500">No runs found</div>
      )}
    </div>
  );
};
